import type { FoundrySpecValidatorRecord } from "../api/foundryV2Client";

function summaryTone(status: "ready" | "watch" | "blocked"): string {
  switch (status) {
    case "ready":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "watch":
      return "border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "border-red-200 bg-red-50 text-red-700";
  }
}

function checkTone(status: "pass" | "watch" | "fail"): string {
  switch (status) {
    case "pass":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "watch":
      return "border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "border-red-200 bg-red-50 text-red-700";
  }
}

function findingTone(severity: "blocking" | "watch" | "info"): string {
  switch (severity) {
    case "blocking":
      return "border-red-200 bg-red-50 text-red-700";
    case "watch":
      return "border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "border-slate-200 bg-slate-50 text-slate-700";
  }
}

type Props = {
  specValidator: FoundrySpecValidatorRecord | null;
  isLoading: boolean;
  error: string | null;
};

export function FoundrySpecValidatorPanel({
  specValidator,
  isLoading,
  error,
}: Props): JSX.Element {
  const report = specValidator?.report;
  const findings = report?.findings.slice(0, 6) ?? [];

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
      data-testid="foundry-spec-validator-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Spec validator</div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">Formal validation before Jarvis gets permission to interpret the spec</h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            This layer checks whether Foundry has produced a spec that is complete, coherent, secure, and operationally stable enough to drive deterministic delivery.
          </p>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {isLoading && !specValidator ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading spec validator...
        </div>
      ) : null}

      {report ? (
        <>
          <div className="mt-5 grid gap-3 xl:grid-cols-[1.2fr_1fr_1fr_0.9fr]">
            <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,#f8fafc_0%,#eef4ff_100%)] p-4">
              <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${summaryTone(report.summary.status)}`}>
                  {report.summary.readinessLabel}
                </span>
                <span className="text-xs text-slate-500">Score {(report.summary.score * 100).toFixed(0)}%</span>
              </div>
              <div className="mt-3 text-sm text-slate-700">{report.summary.headline}</div>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                <span className="rounded-full border border-red-200 bg-white px-2.5 py-1 font-semibold text-red-700">
                  {report.summary.blockerCount} blockers
                </span>
                <span className="rounded-full border border-amber-200 bg-white px-2.5 py-1 font-semibold text-amber-700">
                  {report.summary.watchCount} watchpoints
                </span>
                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700">
                  {report.summary.promotedDecisionCount}/{report.summary.criticalDecisionCount} promoted/critical
                </span>
              </div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                {report.nextMoves.slice(0, 4).map((move) => (
                  <li key={move} className="rounded-lg border border-white/70 bg-white/80 px-3 py-2">
                    {move}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Completeness + decisions</div>
              <div className="mt-3 space-y-2 text-xs text-slate-600">
                <div className={`rounded-xl border px-3 py-2 ${checkTone(report.checks.specCompleteness.status)}`}>
                  Completeness · {report.checks.specCompleteness.status}
                </div>
                <div className={`rounded-xl border px-3 py-2 ${checkTone(report.checks.decisionCoverage.status)}`}>
                  Decision coverage · {report.checks.decisionCoverage.status}
                </div>
                <div>Gaps: {report.checks.specCompleteness.gaps.length}</div>
                <div>Contradictions: {report.checks.specCompleteness.contradictions.length}</div>
                <div>Open critical sections: {report.checks.decisionCoverage.openCriticalSectionIds.length}</div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Security + delivery</div>
              <div className="mt-3 space-y-2 text-xs text-slate-600">
                <div className={`rounded-xl border px-3 py-2 ${checkTone(report.checks.securityPosture.status)}`}>
                  Security posture · {report.checks.securityPosture.status}
                </div>
                <div className={`rounded-xl border px-3 py-2 ${checkTone(report.checks.deliveryReadiness.status)}`}>
                  Delivery readiness · {report.checks.deliveryReadiness.status}
                </div>
                <div>Tier: {report.checks.securityPosture.securityTier}</div>
                <div>Compliance: {report.checks.securityPosture.complianceProfile}</div>
                <div>QA: {report.checks.deliveryReadiness.qaStrategy}</div>
                <div>Release: {report.checks.deliveryReadiness.releaseStrategy}</div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Artifacts</div>
              <div className="mt-3 space-y-2 text-xs">
                <a className="block font-medium text-blue-700 underline" href={specValidator.artifactUrls.specValidator}>
                  spec_validator_report.json
                </a>
              </div>
              <div className="mt-3 text-xs text-slate-600">
                Runtime constraints: {report.checks.runtimeReadiness.runtimeConstraints.length}
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Key findings</div>
              <div className="mt-3 space-y-3">
                {findings.map((finding) => (
                  <div key={finding.id} className="rounded-xl border border-white bg-white px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${findingTone(finding.severity)}`}>
                        {finding.severity}
                      </span>
                      <div className="text-sm font-semibold text-slate-900">{finding.title}</div>
                    </div>
                    <div className="mt-2 text-sm text-slate-700">{finding.summary}</div>
                    {finding.nextAction ? (
                      <div className="mt-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                        Next action: {finding.nextAction}
                      </div>
                    ) : null}
                  </div>
                ))}
                {!findings.length ? (
                  <div className="rounded-xl border border-white bg-white px-4 py-3 text-sm text-slate-600">
                    No validator finding detected.
                  </div>
                ) : null}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Runtime + evidence</div>
              <div className="mt-3 space-y-3">
                <div className="rounded-xl border border-white bg-white px-4 py-3">
                  <div className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${checkTone(report.checks.runtimeReadiness.status)}`}>
                    Runtime readiness · {report.checks.runtimeReadiness.status}
                  </div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">{report.checks.runtimeReadiness.topology}</div>
                  <div className="mt-1 text-xs text-slate-600">
                    Hosting: {report.checks.runtimeReadiness.hostingMode} · build-ready: {report.checks.runtimeReadiness.buildReadyStatus}
                  </div>
                </div>
                <div className="rounded-xl border border-white bg-white px-4 py-3">
                  <div className="text-sm font-semibold text-slate-900">Evidence refs</div>
                  <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                    {report.evidenceRefs.slice(0, 6).map((ref) => (
                      <span key={ref} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-700">
                        {ref}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
